import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { createContext, useContext, useEffect, useId, useRef, useState } from 'react';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

/* ── Button ── */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** default and secondary are one raised button; destructive is danger-solid (ADR-042). */
  variant?:
    | 'default'
    | 'secondary'
    | 'primary'
    | 'danger'
    | 'danger-solid'
    | 'destructive'
    | 'ghost'
    | 'quiet';
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  /** Working: disabled, with a spinner over the label (the label keeps the button's name). */
  busy?: boolean;
}
export function Button({
  variant = 'default',
  size = 'md',
  block,
  busy,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  const look =
    variant === 'secondary' ? 'default' : variant === 'destructive' ? 'danger-solid' : variant;
  return (
    <button
      type={type}
      className={cx(
        'd-btn',
        look !== 'default' && `d-btn--${look}`,
        size !== 'md' && `d-btn--${size}`,
        block && 'd-btn--block',
        className,
      )}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      <span className="d-btn__label">{children}</span>
      {busy && <span className="d-spinner" aria-hidden />}
    </button>
  );
}

/** A square button whose only content is a glyph: `label` is its accessible name (and tooltip). */
export function IconButton({
  label,
  children,
  className,
  ...rest
}: Omit<ButtonProps, 'aria-label'> & { label: string }) {
  return (
    <Button {...rest} className={cx('d-btn--icon', className)} aria-label={label} title={label}>
      <span aria-hidden>{children}</span>
    </Button>
  );
}

/* ── Spinner / skeleton / loading ── */
export function Spinner({ size = 'md', label }: { size?: 'md' | 'lg'; label?: string }) {
  return (
    <span
      className={cx('d-spinner', size === 'lg' && 'd-spinner--lg')}
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  );
}

/** A screen's first load: a short line with a spinner, or placeholder rows. */
export function Loading({ label = 'Loading…', rows }: { label?: string; rows?: number }) {
  if (rows)
    return (
      <div className="d-list" role="status" aria-label={label}>
        {Array.from({ length: rows }, (_, i) => (
          <span key={i} className="d-skeleton d-skeleton--row" />
        ))}
      </div>
    );
  return (
    <div className="d-loading" role="status">
      <span className="d-spinner" aria-hidden />
      <span>{label}</span>
    </div>
  );
}

/* ── Empty / error ── */
export function EmptyState({
  title,
  children,
  action,
  testId,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  testId?: string;
}) {
  return (
    <div className="d-empty" data-testid={testId}>
      <div className="d-empty__title">{title}</div>
      {children && <div className="d-muted">{children}</div>}
      {action}
    </div>
  );
}

/** Something could not be read or done: what happened, in words for staff, and a way to try again. */
export function ErrorState({
  title = 'Could not load this',
  message,
  onRetry,
  retryLabel = 'Try again',
  testId,
}: {
  title?: string;
  message?: ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  testId?: string;
}) {
  return (
    <div className="d-state-error" role="alert" data-testid={testId}>
      <div className="d-state-error__title">{title}</div>
      {message && <div>{message}</div>}
      {onRetry && (
        <Button size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

/* ── Notice / banner ── */
export function Notice({
  tone = 'neutral',
  title,
  children,
  action,
  role,
  testId,
}: {
  tone?: Exclude<Tone, 'accent'>;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  role?: 'status' | 'alert';
  testId?: string;
}) {
  return (
    <div
      className={cx('d-notice', tone !== 'neutral' && `d-notice--${tone}`)}
      role={role}
      data-testid={testId}
    >
      {tone !== 'neutral' && <Shape shape={SHAPE_BY_TONE[tone]} />}
      <div className="d-notice__body">
        {title && <div className="d-strong">{title}</div>}
        {children}
      </div>
      {action}
    </div>
  );
}

/** A sticky strip across the top of a screen: the connection, or anything else that holds work up. */
export function Banner({
  tone = 'danger',
  title,
  children,
  testId,
  state,
}: {
  tone?: 'danger' | 'warning';
  title: string;
  children?: ReactNode;
  testId?: string;
  state?: string;
}) {
  return (
    <div
      className={cx('d-banner', tone === 'warning' && 'd-banner--warning')}
      role="status"
      aria-live="polite"
      data-testid={testId}
      data-state={state}
    >
      <Shape shape={SHAPE_BY_TONE[tone]} />
      <span className="d-banner__title">{title}</span>
      {children && <span>{children}</span>}
    </div>
  );
}

/* ── Pills (segmented control) ── */
export interface PillOption<T extends string> {
  value: T;
  label: string;
}
export function Pills<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: PillOption<T>[];
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  return (
    <div className="d-pills" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="d-pill"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ── Tabs ── */
export function Tabs<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  idBase,
}: {
  value: T;
  options: PillOption<T>[];
  onChange: (v: T) => void;
  ariaLabel: string;
  /** Tab ids are `${idBase}-tab-${value}`; a panel names its tab with aria-labelledby. */
  idBase?: string;
}) {
  const fallback = useId();
  const base = idBase ?? fallback;
  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + (e.key === 'ArrowRight' ? 1 : -1) + options.length) % options.length];
    if (next) {
      onChange(next.value);
      document.getElementById(`${base}-tab-${next.value}`)?.focus();
    }
  };
  return (
    <div className="d-tabs" role="tablist" aria-label={ariaLabel} onKeyDown={onKey}>
      {options.map((o) => (
        <button
          key={o.value}
          id={`${base}-tab-${o.value}`}
          type="button"
          role="tab"
          className="d-tab"
          aria-selected={o.value === value}
          tabIndex={o.value === value ? 0 : -1}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ── Surface ── */
export function Surface({
  children,
  pad = 'md',
  flat,
  className,
}: {
  children: ReactNode;
  pad?: 'none' | 'sm' | 'md';
  flat?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'd-surface',
        flat && 'd-surface--flat',
        pad === 'md' && 'd-surface--pad',
        pad === 'sm' && 'd-surface--pad-sm',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** A section's heading with its actions on the same line (they wrap below it on a phone). */
export function SectionHead({
  title,
  level = 2,
  children,
}: {
  title: ReactNode;
  level?: 2 | 3;
  children?: ReactNode;
}) {
  const H = level === 2 ? 'h2' : 'h3';
  return (
    <div className="d-section-head">
      <H>{title}</H>
      {children && <div className="d-actions">{children}</div>}
    </div>
  );
}

/* ── Stat / key-value ── */
export function Stat({
  label,
  value,
  tone,
  testId,
}: {
  label: ReactNode;
  value: ReactNode;
  tone?: Tone;
  testId?: string;
}) {
  return (
    <div className={cx('d-stat', tone && tone !== 'neutral' && `d-stat--${tone}`)}>
      <div className="d-stat__label">{label}</div>
      <div className="d-stat__value" data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

export function KvRow({
  label,
  value,
  total,
  testId,
}: {
  label: ReactNode;
  value: ReactNode;
  total?: boolean;
  testId?: string;
}) {
  return (
    <div className={cx('d-kv__row', total && 'd-kv__row--total')}>
      <span className="d-muted">{label}</span>
      <span data-testid={testId}>{value}</span>
    </div>
  );
}

/* ── Field / Input / Select / Textarea ── */
export function Field({
  label,
  help,
  error,
  children,
}: {
  label: string;
  help?: string;
  error?: string | null;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="d-field">
      <label className="d-label" htmlFor={id}>
        {label}
      </label>
      {children(id)}
      {error ? (
        <div className="d-error" role="alert">
          {error}
        </div>
      ) : help ? (
        <div className="d-help">{help}</div>
      ) : null}
    </div>
  );
}
export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx('d-input', className)} {...rest} />;
}
export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx('d-select', className)} {...rest}>
      {children}
    </select>
  );
}
export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx('d-textarea', className)} {...rest} />;
}

/* ── Checkbox (a native checkbox, larger, with its label) ── */
export function Checkbox({
  checked,
  onChange,
  label,
  disabled,
  testId,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <label className="d-check">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        data-testid={testId}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

/* ── Toggle ── */
export function Toggle({
  checked,
  onChange,
  label,
  disabled,
  labelledBy,
  describedBy,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
  /** The element that names what the switch turns on (its own label then says only On or Off). */
  labelledBy?: string;
  describedBy?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      className="d-toggle"
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className="d-toggle__track">
        <span className="d-toggle__thumb" />
      </span>
      <span>{label}</span>
    </button>
  );
}

/* ── Badge ── */
export function Badge({
  tone = 'neutral',
  dot,
  outline,
  children,
  testId,
}: {
  tone?: Tone;
  dot?: boolean;
  outline?: boolean;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <span
      className={cx(
        'd-badge',
        tone !== 'neutral' && `d-badge--${tone}`,
        outline && 'd-badge--outline',
      )}
      data-testid={testId}
    >
      {dot && <span className="d-dot" aria-hidden />}
      {children}
    </span>
  );
}

/* ── Shape: what a state looks like besides its colour (ADR-037, ADR-042) ── */
export type ShapeKind = 'dot' | 'ring' | 'triangle' | 'square' | 'diamond';
/** ● done or fine · ○ waiting · ▲ needs attention · ■ stopped · ◆ for information */
export const SHAPE_BY_TONE: Record<Tone, ShapeKind> = {
  neutral: 'ring',
  success: 'dot',
  accent: 'dot',
  info: 'diamond',
  warning: 'triangle',
  danger: 'triangle',
};
export function Shape({ shape }: { shape: ShapeKind }) {
  return <span className={`d-shape d-shape--${shape}`} aria-hidden />;
}

/** A state in a word and a shape (● READY, ▲ LATE, ○ QUEUED): never colour alone. */
export function StatusChip({
  tone = 'neutral',
  shape,
  size = 'md',
  children,
  testId,
}: {
  tone?: Tone;
  shape?: ShapeKind;
  size?: 'md' | 'lg';
  children: ReactNode;
  testId?: string;
}) {
  return (
    <span
      className={cx('d-chip', `d-chip--${tone}`, size === 'lg' && 'd-chip--lg')}
      data-testid={testId}
    >
      <Shape shape={shape ?? SHAPE_BY_TONE[tone]} />
      <span>{children}</span>
    </span>
  );
}

/* ── Segmented control: one choice of a few, as radio buttons (arrow keys move the choice) ── */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  testId,
}: {
  value: T;
  options: PillOption<T>[];
  onChange: (v: T) => void;
  ariaLabel: string;
  testId?: string;
}) {
  const base = useId();
  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const step =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? 1
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? -1
          : 0;
    if (!step) return;
    e.preventDefault();
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + step + options.length) % options.length];
    if (next) {
      onChange(next.value);
      document.getElementById(`${base}-${next.value}`)?.focus();
    }
  };
  return (
    <div
      className="d-segmented"
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={onKey}
      data-testid={testId}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            id={`${base}-${o.value}`}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            className="d-segmented__item"
            onClick={() => onChange(o.value)}
            data-testid={testId ? `${testId}-${o.value}` : undefined}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ── Section: a titled group of content on a raised container ── */
export function Section({
  title,
  description,
  actions,
  children,
  level = 2,
  flat,
  testId,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  level?: 2 | 3;
  flat?: boolean;
  testId?: string;
}) {
  const H = level === 2 ? 'h2' : 'h3';
  return (
    <section className={cx('d-section', flat && 'd-section--flat')} data-testid={testId}>
      {(title || actions) && (
        <div className="d-section__head">
          <div className="d-section__titles">
            {title && <H className="d-section__title">{title}</H>}
            {description && <p className="d-section__desc">{description}</p>}
          </div>
          {actions && <div className="d-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/* ── Metric card: one figure with its label, a small icon chip and a note ── */
export function MetricCard({
  label,
  value,
  icon,
  note,
  tone,
  testId,
}: {
  label: ReactNode;
  value: ReactNode;
  icon?: ReactNode;
  note?: ReactNode;
  tone?: Tone;
  testId?: string;
}) {
  return (
    <div className={cx('d-metric', tone && tone !== 'neutral' && `d-metric--${tone}`)}>
      <div className="d-metric__head">
        {icon && (
          <span className="d-metric__icon" aria-hidden>
            {icon}
          </span>
        )}
        <span className="d-metric__label">{label}</span>
      </div>
      <div className="d-metric__value" data-testid={testId}>
        {value}
      </div>
      {note && <div className="d-metric__note">{note}</div>}
    </div>
  );
}

/* ── Skeleton: the shape of what is loading ── */
export function Skeleton({
  kind = 'lines',
  count = 3,
  label = 'Loading…',
}: {
  kind?: 'lines' | 'rows' | 'cards';
  count?: number;
  label?: string;
}) {
  return (
    <div className={cx('d-skeletons', `d-skeletons--${kind}`)} role="status" aria-label={label}>
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          className={cx(
            'd-skeleton',
            kind === 'rows' && 'd-skeleton--row',
            kind === 'cards' && 'd-skeleton--card',
          )}
        />
      ))}
    </div>
  );
}

/* ── Page header ── */
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="d-page__head">
      <div className="d-page__title">
        {eyebrow && <div className="d-eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {subtitle && <p className="d-muted">{subtitle}</p>}
      </div>
      {actions && <div className="d-actions">{actions}</div>}
    </header>
  );
}

/* ── Coming later ── */
export function ComingLater({
  module,
  phase,
  detail,
}: {
  module: string;
  phase: string;
  detail?: string;
}) {
  return (
    <div className="d-coming" role="status">
      <div className="d-coming__tag">Coming in {phase}</div>
      <h2>{module}</h2>
      {detail && <p className="d-muted">{detail}</p>}
    </div>
  );
}

/* ── PIN pad ── */
export function PinPad({
  length = 6,
  minLength = 4,
  onSubmit,
  onCancel,
  disabled,
  error,
  title,
}: {
  length?: number;
  minLength?: number;
  onSubmit: (pin: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
  error?: string | null;
  title?: string;
}) {
  const [pin, setPin] = useState('');
  const push = (d: string) => {
    if (!disabled && pin.length < length) setPin(pin + d);
  };
  const submit = () => {
    if (pin.length >= minLength && !disabled) onSubmit(pin);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (disabled) return;
      if (/^\d$/.test(e.key)) setPin((p) => (p.length < length ? p + e.key : p));
      else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
      else if (e.key === 'Enter')
        setPin((p) => {
          if (p.length >= minLength) onSubmit(p);
          return p;
        });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled, length, minLength, onSubmit]);
  return (
    <div className="d-stack" data-testid="pinpad">
      {title && <h2 className="d-pinpad__title">{title}</h2>}
      <div className="d-pindots" role="img" aria-label={`${pin.length} digits entered`}>
        {Array.from({ length }, (_, i) => (
          <span key={i} className={cx('d-pindots__dot', i < pin.length && 'd-pindots__dot--on')} />
        ))}
      </div>
      {error && (
        <div className="d-error d-pinpad__error" role="alert">
          {error}
        </div>
      )}
      <div className="d-pinpad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button
            key={d}
            type="button"
            className="d-pinpad__key"
            onClick={() => push(d)}
            disabled={disabled}
            aria-label={`digit ${d}`}
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          className="d-pinpad__key"
          onClick={() => setPin(pin.slice(0, -1))}
          disabled={disabled}
          aria-label="delete"
        >
          ⌫
        </button>
        <button
          type="button"
          className="d-pinpad__key"
          onClick={() => push('0')}
          disabled={disabled}
          aria-label="digit 0"
        >
          0
        </button>
        <button
          type="button"
          className="d-pinpad__key d-pinpad__key--confirm"
          onClick={submit}
          disabled={disabled || pin.length < minLength}
          aria-label="confirm PIN"
        >
          ✓
        </button>
      </div>
      {onCancel && (
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      )}
    </div>
  );
}

/* ── Sheet ──
   A bottom sheet on a phone, a centred dialog from 720px. Escape closes the sheet on top only; the
   page behind does not scroll; focus goes into the sheet when it opens (unless a field in it took it)
   and back to what had it when it closes. */
const sheetStack: number[] = [];
let sheetSeq = 0;
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * What every open sheet shows under its title, given by the app: the connection notice while the
 * server cannot be reached (ADR-037), so a sheet never shows a paused action without its reason.
 */
export const SheetNoticeContext = createContext<ReactNode>(null);

/**
 * True while the server cannot be reached (ADR-037), given by the app. Every ConfirmSheet answer
 * records something on the server, so its confirm button is paused meanwhile.
 */
export const ActionsPausedContext = createContext(false);

export function Sheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  const notice = useContext(SheetNoticeContext);
  const ref = useRef<HTMLDivElement>(null);
  // the latest onClose for the key handler, without re-running the open/close effect
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const id = ++sheetSeq;
    sheetStack.push(id);
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.classList.add('d-scroll-locked');
    // a field that asked for focus (autoFocus) keeps it; otherwise the dialog itself takes it
    if (ref.current && !ref.current.contains(document.activeElement)) ref.current.focus();
    const onKey = (e: KeyboardEvent) => {
      if (sheetStack[sheetStack.length - 1] !== id) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key === 'Tab' && ref.current) {
        const items = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
          (el) => el.offsetParent !== null,
        );
        if (items.length === 0) return;
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (
          e.shiftKey &&
          (document.activeElement === first || document.activeElement === ref.current)
        ) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const at = sheetStack.indexOf(id);
      if (at >= 0) sheetStack.splice(at, 1);
      if (sheetStack.length === 0) document.body.classList.remove('d-scroll-locked');
      if (opener && opener.isConnected) opener.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="d-sheet__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        className="d-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className="d-sheet__head">
          <h2 id={titleId}>{title}</h2>
          <Button
            variant="quiet"
            size="sm"
            className="d-btn--icon"
            onClick={onClose}
            aria-label="close"
          >
            <span aria-hidden>✕</span>
          </Button>
        </div>
        {notice}
        {children}
      </div>
    </div>
  );
}

/** A yes/no before something that cannot be taken back, or costs money: says what will happen. */
export function ConfirmSheet({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'danger',
  busy,
  error,
  onConfirm,
  onClose,
  testId,
}: {
  open: boolean;
  title: string;
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
  testId?: string;
}) {
  const paused = useContext(ActionsPausedContext);
  return (
    <Sheet open={open} onClose={onClose} title={title}>
      {children && <div className="d-stack">{children}</div>}
      {error && (
        <div className="d-error" role="alert">
          {error}
        </div>
      )}
      <div className="d-sheet__actions">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          variant={tone === 'danger' ? 'danger-solid' : 'primary'}
          onClick={onConfirm}
          busy={busy}
          disabled={paused}
          data-testid={testId}
        >
          {confirmLabel}
        </Button>
      </div>
    </Sheet>
  );
}

/* ── Toasts ── */
export interface ToastItem {
  id: number;
  text: string;
  tone?: 'neutral' | 'success' | 'danger';
}
/** One polite live region reads each toast out once; the toasts carry no role of their own, since a
 * live region inside another is read twice by some screen readers. */
export function Toasts({ items }: { items: ToastItem[] }) {
  return (
    <div className="d-toasts" aria-live="polite">
      {items.map((t) => (
        <div
          key={t.id}
          className={cx('d-toast', t.tone && t.tone !== 'neutral' && `d-toast--${t.tone}`)}
        >
          <Shape shape={SHAPE_BY_TONE[t.tone ?? 'neutral']} />
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Row ── */
export function Row({
  title,
  meta,
  right,
  onClick,
  testId,
}: {
  title: ReactNode;
  meta?: ReactNode;
  right?: ReactNode;
  onClick?: () => void;
  testId?: string;
}) {
  const inner = (
    <>
      <div className="d-row__main">
        <div className="d-row__title">{title}</div>
        {meta && <div className="d-row__meta">{meta}</div>}
      </div>
      {right}
    </>
  );
  return onClick ? (
    <button type="button" className="d-row d-row--button" onClick={onClick} data-testid={testId}>
      {inner}
    </button>
  ) : (
    <div className="d-row" data-testid={testId}>
      {inner}
    </div>
  );
}
